// ─────────────────────────────────────────────────────────────────────────────
// REQ-109 — Constelación de Fundamentales (visualización SVG interactiva).
//
// Portada de la maqueta (features/brillamos/Constelacion.tsx) SIN framer-motion:
// SVG autocontenido (solo las estrellas decorativas son símbolos inline) + animación CSS.
// Los nodos se alimentan del CATÁLOGO REAL (useRecognitionCatalog), ya filtrado por el llamador
// con `fundamentalesOfrecibles` (mismo filtro que el riel de ReconocerView — bug #21): nombre,
// descripción e icono localizados — el icono usa `getFundamentalIcon` (misma resolución que
// FundamentalesLibrary: imagen/icono subido en el catálogo → asset de marca por color →
// fallback por índice), antes hardcodeado por posición y ajeno al catálogo. Al pasar el
// mouse sobre un nodo se muestra un panel con número (01/04), nombre y descripción; el nodo
// deja de animarse en hover.
//
// F5 (H-68): el panel se posiciona por JS, IGUAL que el demo (Constelacion.tsx del demo,
// líneas 24-45): se calcula `left`/`top` a partir del `getBoundingClientRect()` del nodo
// bajo el mouse, centrado horizontalmente sobre él y clampeado al ancho del contenedor;
// si no cabe arriba (panel muy alto vs. línea de nodos) se muestra debajo. Antes era
// declarativo (bottom:-6px fijo, siempre centrado en el componente completo) — eso hacía
// que el panel NO siguiera al nodo activo como en el demo. El panel se mantiene SIEMPRE
// montado (oculto con opacity:0 vía `.constelacion__panel` sin `.is-visible`), igual que
// el demo (un único nodo DOM reutilizado, nunca desmontado), para poder medir su alto real
// antes de la primera interacción. Ver razonamiento completo en ESTADO.md (decisión F5 §18).
//
// BUG (Julio, 2026-08-20) — el número de nodos estaba fijo a 4: `NODE_X`/`NODE_R` eran arreglos
// de exactamente 4 posiciones y un `.slice(0, 4)` descartaba cualquier Fundamental extra en
// silencio. "Fundamentales disponibles" (Gestión → Brillamos) no podía nunca mostrar 5+ ni menos
// de 4 aunque el admin los hubiera apagado — el filtro llegaba desde el llamador, pero el
// COMPONENTE ignoraba cuántos le llegaban de verdad. Ahora la cantidad de nodos es la que traiga
// `funds` (ya filtrado por el llamador): 4 conserva el layout ORIGINAL de la maqueta pixel a
// pixel (`DEFAULT_NODE_X`/`DEFAULT_NODE_R`/`DEFAULT_STARS`); cualquier otro número (menos o más de
// 4) distribuye los nodos de forma genérica y pareja a lo largo de la línea (`layoutNodes`), con
// un radio que se reduce ligeramente a partir del 5° para que no se encimen.
// ─────────────────────────────────────────────────────────────────────────────
import { useRef, useState } from 'react';
import { getFundamentalName, getFundamentalColor, pickLocalized } from '@/api/recognition';
import type { FundamentalDto } from '@/api/recognition';
import { getFundamentalIcon } from './fundamentalAssets';

const VIEW_W = 1200;
const NODE_Y = 140;

/** Layout ORIGINAL de la maqueta — se preserva pixel a pixel para el caso más común (4). */
const DEFAULT_NODE_X = [220, 540, 870, 1170];
const DEFAULT_NODE_R = [55, 55, 60, 60];

interface StarDeco {
  x: number;
  symbol: 'c-deco-star-5' | 'c-deco-star-4' | 'c-deco-star-8';
  size: number;
  drift: string;
  delay: string;
  rotate: 'rotate-slow' | 'rotate-reverse';
}

/** Estrellas decorativas ORIGINALES de la maqueta, entre los 4 nodos del layout por defecto. */
const DEFAULT_STARS: StarDeco[] = [
  { x: 80, symbol: 'c-deco-star-5', size: 40, drift: 'drift-2', delay: 'delay-2', rotate: 'rotate-slow' },
  { x: 395, symbol: 'c-deco-star-4', size: 60, drift: 'drift-3', delay: 'delay-3', rotate: 'rotate-slow' },
  { x: 720, symbol: 'c-deco-star-8', size: 48, drift: 'drift-1', delay: 'delay-6', rotate: 'rotate-reverse' },
  { x: 1040, symbol: 'c-deco-star-5', size: 44, drift: 'drift-2', delay: 'delay-6', rotate: 'rotate-reverse' },
];

const STAR_SYMBOLS: StarDeco['symbol'][] = ['c-deco-star-5', 'c-deco-star-4', 'c-deco-star-8'];

/**
 * Posiciones X y radios de `count` nodos. `count === 4` reproduce el layout original de la
 * maqueta; cualquier otro valor los reparte de forma pareja, con margen proporcional al radio
 * para que ningún nodo quede cortado por el borde del viewBox. El radio se reduce 4px por cada
 * nodo extra a partir del 4° (nunca baja de 36px: sigue siendo un blanco cómodo de hover/foco).
 */
function layoutNodes(count: number): { x: number[]; r: number[] } {
  if (count === 4) return { x: DEFAULT_NODE_X, r: DEFAULT_NODE_R };
  if (count <= 0) return { x: [], r: [] };
  const r = Math.max(36, 60 - Math.max(0, count - 4) * 4);
  if (count === 1) return { x: [VIEW_W / 2], r: [r] };
  const margin = r + 60;
  const step = (VIEW_W - margin * 2) / (count - 1);
  return {
    x: Array.from({ length: count }, (_, i) => margin + step * i),
    r: Array.from({ length: count }, () => r),
  };
}

/** Una estrella decorativa a la mitad de cada tramo entre nodos consecutivos. */
function layoutStars(nodeX: number[]): StarDeco[] {
  if (nodeX.length === 4 && nodeX[0] === DEFAULT_NODE_X[0] && nodeX[3] === DEFAULT_NODE_X[3]) {
    return DEFAULT_STARS;
  }
  const stars: StarDeco[] = [];
  for (let i = 0; i < nodeX.length - 1; i++) {
    stars.push({
      x: (nodeX[i] + nodeX[i + 1]) / 2,
      symbol: STAR_SYMBOLS[i % STAR_SYMBOLS.length],
      size: 40 + (i % 3) * 10,
      drift: `drift-${(i % 4) + 1}`,
      delay: `delay-${(i % 7) + 1}`,
      rotate: i % 2 === 0 ? 'rotate-slow' : 'rotate-reverse',
    });
  }
  return stars;
}

interface Props {
  funds: FundamentalDto[];
  lang: string;
}

export function Constelacion({ funds, lang }: Readonly<Props>) {
  const [active, setActive] = useState<number | null>(null);
  const [panelPos, setPanelPos] = useState<{ left: number; top: number } | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const shapeRefs = useRef<Array<SVGGElement | null>>([]);
  // `funds` ya llega filtrado por el llamador (activos y no deshabilitados para este programa) —
  // el componente pinta TODOS los que le lleguen, sin tope fijo.
  const nodes = funds;
  if (nodes.length === 0) return null;

  const { x: nodeX, r: nodeR } = layoutNodes(nodes.length);
  const stars = layoutStars(nodeX);

  // Réplica literal del cálculo del demo (Constelacion.tsx, `showPanel`): el panel se
  // centra horizontalmente sobre el nodo y se ubica arriba salvo que no quepa, en cuyo
  // caso se muestra debajo. Clampeado a los bordes del contenedor.
  const positionPanel = (idx: number) => {
    const root = rootRef.current;
    const shape = shapeRefs.current[idx];
    const panel = panelRef.current;
    if (!root || !shape) return;
    const rect = shape.getBoundingClientRect();
    const rootRect = root.getBoundingClientRect();
    const panelW = 280;
    const panelH = panel?.offsetHeight || 160;
    let x = rect.left - rootRect.left + rect.width / 2 - panelW / 2;
    let y = rect.top - rootRect.top - panelH - 20;
    if (y < -120) y = rect.bottom - rootRect.top + 20;
    x = Math.max(8, Math.min(x, rootRect.width - panelW - 8));
    setPanelPos({ left: x, top: y });
  };

  const showPanel = (idx: number) => { setActive(idx); positionPanel(idx); };
  const hidePanel = () => setActive(null);

  // El panel queda SIEMPRE montado (oculto vía CSS) para poder medir su alto real antes
  // de la primera interacción, igual que el demo; muestra el último Fundamental activo
  // (o el primero como contenido inicial, nunca vacío).
  const panelFund = nodes[active ?? 0];
  const panelIdx = active ?? 0;
  const panelColor = getFundamentalColor(panelFund, panelIdx);
  const panelDesc = pickLocalized(panelFund.descriptions, lang) || panelFund.description || '';

  return (
    <div className={`constelacion ${active != null ? 'is-paused' : ''}`} ref={rootRef}>
      <svg className="constelacion__svg" viewBox={`0 0 ${VIEW_W} 280`} preserveAspectRatio="xMidYMid meet" role="img"
        aria-label="Constelación de Fundamentales">
        <defs>
          <symbol id="c-deco-star-5" viewBox="-50 -50 100 100">
            <path d="M 0,-48 L 14,-15 L 47,-15 L 21,5 L 30,38 L 0,18 L -30,38 L -21,5 L -47,-15 L -14,-15 Z"/>
          </symbol>
          <symbol id="c-deco-star-4" viewBox="-50 -50 100 100">
            <path d="M 0,-48 C 4,-20 4,-20 20,-12 C 36,-4 36,-4 48,0 C 36,4 36,4 20,12 C 4,20 4,20 0,48 C -4,20 -4,20 -20,12 C -36,4 -36,4 -48,0 C -36,-4 -36,-4 -20,-12 C -4,-20 -4,-20 0,-48 Z"/>
          </symbol>
          <symbol id="c-deco-star-8" viewBox="-50 -50 100 100">
            <path d="M 0,-46 L 10,-18 L 32.5,-32.5 L 18,-10 L 46,0 L 18,10 L 32.5,32.5 L 10,18 L 0,46 L -10,18 L -32.5,32.5 L -18,10 L -46,0 L -18,-10 L -32.5,-32.5 L -10,-18 Z"/>
          </symbol>
        </defs>

        <line className="constelacion__line" x1="0" y1={NODE_Y} x2={VIEW_W} y2={NODE_Y} />

        {/* Estrellas decorativas entre nodos */}
        {stars.map((s, i) => (
          <g key={`star-${i}`} transform={`translate(${s.x}, ${NODE_Y})`}>
            <g className={`${s.drift} ${s.delay}`}><g className={s.rotate}>
              <use href={`#${s.symbol}`} x={-s.size / 2} y={-s.size / 2} width={s.size} height={s.size} fill="var(--const-line)" />
            </g></g>
          </g>
        ))}

        {/* Nodos = Fundamentales del catálogo (ya filtrados por el llamador). El icono es la
            imagen REAL del catálogo (getFundamentalIcon: imagen subida → icono subido → asset de
            marca por color → fallback por índice), igual que FundamentalesLibrary. Ya trae su
            propio disco de color + glifo (ver fundamentalAssets.ts), por eso reemplaza al
            círculo de color. */}
        {nodes.map((f, i) => {
          const size = nodeR[i] * 2;
          // `delay-${(i+3)%7+1}` reproduce EXACTO `delay-4..7` de la maqueta para i=0..3 y sigue
          // cicleando (1..7) para cualquier nodo extra, en vez de crecer sin tope (`delay-8` no
          // existe en CSS).
          return (
            <g key={f.id} transform={`translate(${nodeX[i]}, ${NODE_Y})`}>
              <g className={`drift-${(i % 4) + 1} delay-${((i + 3) % 7) + 1}`}>
                <g
                  ref={(el) => { shapeRefs.current[i] = el; }}
                  className="constelacion__shape"
                  tabIndex={0}
                  role="button"
                  aria-label={getFundamentalName(f, lang)}
                  onMouseEnter={() => showPanel(i)}
                  onMouseLeave={hidePanel}
                  onFocus={() => showPanel(i)}
                  onBlur={hidePanel}
                >
                  <g className="constelacion__bg">
                    <image
                      href={getFundamentalIcon(f, i)}
                      x={-nodeR[i]}
                      y={-nodeR[i]}
                      width={size}
                      height={size}
                    />
                  </g>
                </g>
              </g>
            </g>
          );
        })}
      </svg>

      <div
        ref={panelRef}
        className={`constelacion__panel ${active != null ? 'is-visible' : ''}`}
        style={{
          '--const-panel-accent': panelColor,
          left: panelPos?.left ?? 0,
          top: panelPos?.top ?? 0,
        } as React.CSSProperties}
      >
        <span className="constelacion__index">{String(panelIdx + 1).padStart(2, '0')} / {String(nodes.length).padStart(2, '0')}</span>
        <div className="constelacion__badge">{panelFund.code || getFundamentalName(panelFund, lang)}</div>
        <h3 className="constelacion__title">{getFundamentalName(panelFund, lang)}</h3>
        {panelDesc && <p className="constelacion__desc">{panelDesc}</p>}
      </div>
    </div>
  );
}

export default Constelacion;
